import { Play, X } from "lucide-react";
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { Paint } from "@/document/model/styles";
import type {
  ScriptParam,
  ScriptParamValue,
} from "@/scripting/scriptParams";
import {
  defaultValuesForParams,
  isScriptGradientParamValue,
  scriptSourceSupportsParamLivePreview,
} from "@/scripting/scriptParams";
import {
  colorParamToPaint,
  gradientParamToPaint,
  paintToColorParam,
  paintToGradientParam,
} from "@/scripting/scriptParamPaint";
import { ColorPickerPopover } from "./right-panel/ColorPickerPopover";
import { paintPreviewBackground, paintValueLabel } from "./right-panel/paintStyles";

export interface ScriptParamsModalSession {
  scriptId: string;
  scriptName: string;
  /** Where to resolve the script from when confirming / previewing. */
  scriptLibrarySource?: "document" | "editor";
  contextFrameId?: string;
  params: ScriptParam[];
  values: Record<string, ScriptParamValue>;
  /** When false, omit live-preview UI (`update`/`draw` or `-- @nopreview`). */
  supportsLivePreview: boolean;
  /** Ghost-render param changes onto the context frame before Run. Default on. */
  livePreview: boolean;
}

interface ScriptParamsModalProps {
  session: ScriptParamsModalSession;
  onChangeValues: (values: Record<string, ScriptParamValue>) => void;
  onChangeLivePreview: (livePreview: boolean) => void;
  onRun: () => void | Promise<void>;
  onClose: () => void;
}

interface PanelGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

const storageKey = "curva:script-params-modal";
const viewportMargin = 12;
const minPanelWidth = 320;
const minPanelHeight = 220;

export function ScriptParamsModal({
  session,
  onChangeValues,
  onChangeLivePreview,
  onRun,
  onClose,
}: ScriptParamsModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; panelX: number; panelY: number } | undefined>(undefined);
  const [geometry, setGeometry] = useState<PanelGeometry>(() => initialPanelGeometry());
  const [running, setRunning] = useState(false);
  const [paintTarget, setPaintTarget] = useState<{ name: string; mode: "color" | "gradient" } | undefined>(undefined);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (paintTarget) {
          setPaintTarget(undefined);
          return;
        }
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, paintTarget]);

  useEffect(() => {
    const handleResize = () => {
      setGeometry(current => {
        const next = clampPanelGeometry(current);
        storePanelGeometry(next);
        return next;
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function setValue(name: string, value: ScriptParamValue): void {
    onChangeValues({ ...session.values, [name]: value });
  }

  function handleHeaderPointerDown(event: ReactPointerEvent<HTMLDivElement>): void {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      panelX: geometry.x,
      panelY: geometry.y,
    };
  }

  function handleHeaderPointerMove(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setGeometry(clampPanelGeometry({
      ...geometry,
      x: drag.panelX + event.clientX - drag.startX,
      y: drag.panelY + event.clientY - drag.startY,
    }));
  }

  function handleHeaderPointerUp(event: ReactPointerEvent<HTMLDivElement>): void {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = undefined;
    storePanelGeometry(geometry);
  }

  const activePaintParam = paintTarget
    ? session.params.find(param => param.name === paintTarget.name)
    : undefined;
  const activePaintValue = paintTarget
    ? (session.values[paintTarget.name] ?? activePaintParam?.defaultValue)
    : undefined;
  const activePaint: Paint | undefined = paintTarget && activePaintValue !== undefined
    ? (paintTarget.mode === "color"
      ? colorParamToPaint(String(activePaintValue))
      : gradientParamToPaint(
        isScriptGradientParamValue(activePaintValue)
          ? activePaintValue
          : paintToGradientParam(colorParamToPaint(String(activePaintValue))),
      ))
    : undefined;

  return (
    <>
      <section
        ref={panelRef}
        className="fixed z-[1100] flex max-h-[min(560px,calc(100vh-24px))] min-h-[220px] w-[min(380px,calc(100vw-24px))] flex-col overflow-hidden rounded-md border border-white/10 bg-[#171b20] shadow-2xl shadow-black/45"
        style={{ left: geometry.x, top: geometry.y, width: geometry.width }}
        aria-label={`Script parameters: ${session.scriptName}`}
      >
        <div
          className="flex h-8 shrink-0 cursor-move items-center justify-between border-b border-white/10 bg-[#20262d] px-2 text-[12px] font-medium text-slate-300"
          onPointerDown={handleHeaderPointerDown}
          onPointerMove={handleHeaderPointerMove}
          onPointerUp={handleHeaderPointerUp}
          onPointerCancel={handleHeaderPointerUp}
        >
          <span className="truncate pr-2">{session.scriptName}</span>
          <button
            type="button"
            className="flex size-6 items-center justify-center rounded text-slate-400 hover:bg-white/[0.08] hover:text-slate-100"
            aria-label="Close script parameters"
            onPointerDown={event => event.stopPropagation()}
            onClick={onClose}
          >
            <X className="size-3.5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-auto px-3 py-3">
          {session.params.map(param => (
            <ParamControl
              key={param.name}
              param={param}
              value={session.values[param.name] ?? param.defaultValue}
              onChange={value => setValue(param.name, value)}
              onOpenPaint={mode => setPaintTarget({ name: param.name, mode })}
            />
          ))}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-white/10 bg-[#20262d] px-3 py-2">
          {session.supportsLivePreview ? (
            <label className="flex min-w-0 cursor-pointer items-center gap-2 text-[11px] text-slate-400 hover:text-slate-200">
              <input
                type="checkbox"
                className="size-3.5 rounded border-white/20 bg-[#171b20] text-sky-500 focus:ring-sky-500/40"
                checked={session.livePreview}
                onChange={event => onChangeLivePreview(event.target.checked)}
              />
              <span className="truncate">Live preview script result</span>
            </label>
          ) : (
            <span />
          )}
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              className="rounded px-2.5 py-1 text-[12px] text-slate-400 hover:bg-white/[0.08] hover:text-slate-100"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="flex items-center gap-1 rounded bg-sky-500/90 px-2.5 py-1 text-[12px] font-medium text-slate-950 hover:bg-sky-400 disabled:opacity-50"
              disabled={running}
              onClick={() => {
                setRunning(true);
                void Promise.resolve(onRun()).finally(() => setRunning(false));
              }}
            >
              <Play className="size-3.5" />
              Run
            </button>
          </div>
        </div>
      </section>

      {paintTarget && activePaint && activePaintParam ? (
        <ColorPickerPopover
          title={activePaintParam.label}
          paint={activePaint}
          allowedModes={paintTarget.mode === "color" ? ["solid"] : ["gradient"]}
          zIndexClass="z-[1200]"
          onChange={paint => {
            if (paintTarget.mode === "color") {
              setValue(paintTarget.name, paintToColorParam(paint));
              return;
            }
            setValue(paintTarget.name, paintToGradientParam(paint));
          }}
          onColorSampleRequest={() => undefined}
          onColorSampleModeChange={() => undefined}
          onClose={() => setPaintTarget(undefined)}
        />
      ) : null}
    </>
  );
}

function ParamControl({
  param,
  value,
  onChange,
  onOpenPaint,
}: {
  param: ScriptParam;
  value: ScriptParamValue;
  onChange: (value: ScriptParamValue) => void;
  onOpenPaint: (mode: "color" | "gradient") => void;
}) {
  const fieldId = `script-param-${param.name}`;

  if (param.widget === "color" || param.widget === "gradient") {
    const paint = param.widget === "color"
      ? colorParamToPaint(String(value))
      : gradientParamToPaint(
        isScriptGradientParamValue(value)
          ? value
          : paintToGradientParam(colorParamToPaint(String(value))),
      );
    return (
      <div className="block space-y-1 text-[12px] text-slate-300">
        <span className="block truncate text-slate-400">{param.label}</span>
        <button
          type="button"
          id={fieldId}
          className="flex h-8 w-full items-center gap-2 rounded border border-white/10 bg-[#0f1318] px-2 text-left hover:border-sky-400/40"
          onClick={() => onOpenPaint(param.widget === "color" ? "color" : "gradient")}
        >
          <span
            className="size-5 shrink-0 rounded-sm border border-white/20"
            style={{ background: paintPreviewBackground(paint) }}
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-slate-200">
            {paintValueLabel(paint)}
          </span>
        </button>
      </div>
    );
  }

  if (param.widget === "toggle") {
    const checked = Boolean(value);
    return (
      <label className="flex cursor-pointer items-center justify-between gap-3 text-[12px] text-slate-300">
        <span className="min-w-0 truncate">{param.label}</span>
        <input
          id={fieldId}
          type="checkbox"
          className="size-3.5 accent-sky-400"
          checked={checked}
          onChange={event => onChange(event.target.checked)}
        />
      </label>
    );
  }

  if (param.widget === "dropdown") {
    const options = param.options ?? [];
    return (
      <label className="block space-y-1 text-[12px] text-slate-300">
        <span className="block truncate text-slate-400">{param.label}</span>
        <select
          id={fieldId}
          className="w-full rounded border border-white/10 bg-[#0f1318] px-2 py-1.5 text-slate-100 outline-none focus:border-sky-400/60"
          value={String(value)}
          onChange={event => {
            const selected = options.find(option => String(option.value) === event.target.value);
            onChange(selected?.value ?? event.target.value);
          }}
        >
          {options.length === 0 ? (
            <option value={String(value)}>{String(value)}</option>
          ) : (
            options.map(option => (
              <option key={`${option.label}:${String(option.value)}`} value={String(option.value)}>
                {option.label}
              </option>
            ))
          )}
        </select>
      </label>
    );
  }

  if (param.widget === "textarea") {
    return (
      <label className="block space-y-1 text-[12px] text-slate-300">
        <span className="block truncate text-slate-400">{param.label}</span>
        <textarea
          id={fieldId}
          className="min-h-[72px] w-full resize-y rounded border border-white/10 bg-[#0f1318] px-2 py-1.5 text-slate-100 outline-none focus:border-sky-400/60"
          value={String(value)}
          onChange={event => onChange(event.target.value)}
        />
      </label>
    );
  }

  if (param.widget === "text") {
    return (
      <label className="block space-y-1 text-[12px] text-slate-300">
        <span className="block truncate text-slate-400">{param.label}</span>
        <input
          id={fieldId}
          type="text"
          className="w-full rounded border border-white/10 bg-[#0f1318] px-2 py-1.5 text-slate-100 outline-none focus:border-sky-400/60"
          value={String(value)}
          onChange={event => onChange(event.target.value)}
        />
      </label>
    );
  }

  const numberValue = typeof value === "number" ? value : Number(value);
  const safeNumber = Number.isFinite(numberValue) ? numberValue : (param.min ?? 0);
  const min = param.min ?? 0;
  const max = param.max ?? 1;
  const step = param.step ?? 0.01;

  if (param.widget === "slider") {
    return (
      <label className="block space-y-1 text-[12px] text-slate-300">
        <span className="flex items-center justify-between gap-2">
          <span className="min-w-0 truncate text-slate-400">{param.label}</span>
          <span className="shrink-0 tabular-nums text-slate-200">{formatNumber(safeNumber, step)}</span>
        </span>
        <input
          id={fieldId}
          type="range"
          className="w-full accent-sky-400"
          min={min}
          max={max}
          step={step}
          value={safeNumber}
          onChange={event => onChange(Number(event.target.value))}
        />
      </label>
    );
  }

  return (
    <label className="block space-y-1 text-[12px] text-slate-300">
      <span className="block truncate text-slate-400">{param.label}</span>
      <input
        id={fieldId}
        type="number"
        className="w-full rounded border border-white/10 bg-[#0f1318] px-2 py-1.5 text-slate-100 outline-none focus:border-sky-400/60"
        min={min}
        max={max}
        step={step}
        value={safeNumber}
        onChange={event => {
          const next = Number(event.target.value);
          onChange(Number.isFinite(next) ? next : safeNumber);
        }}
      />
    </label>
  );
}

export function createScriptParamsSession(input: {
  scriptId: string;
  scriptName: string;
  scriptLibrarySource?: "document" | "editor";
  contextFrameId?: string;
  params: ScriptParam[];
  /** Script source used to detect update/draw (disables live preview). */
  source?: string;
}): ScriptParamsModalSession {
  const supportsLivePreview = input.source === undefined
    ? true
    : scriptSourceSupportsParamLivePreview(input.source);
  return {
    scriptId: input.scriptId,
    scriptName: input.scriptName,
    scriptLibrarySource: input.scriptLibrarySource ?? "document",
    contextFrameId: input.contextFrameId,
    params: input.params,
    values: defaultValuesForParams(input.params),
    supportsLivePreview,
    livePreview: supportsLivePreview,
  };
}

function formatNumber(value: number, step: number): string {
  if (Number.isInteger(step) && Number.isInteger(value)) {
    return String(value);
  }
  const decimals = Math.max(0, Math.min(6, (String(step).split(".")[1] ?? "").length));
  return value.toFixed(decimals);
}

function initialPanelGeometry(): PanelGeometry {
  const stored = readStoredPanelGeometry();
  if (stored) {
    return clampPanelGeometry(stored);
  }
  const width = Math.min(380, Math.max(minPanelWidth, window.innerWidth * 0.28));
  const height = Math.min(420, Math.max(minPanelHeight, window.innerHeight * 0.45));
  return clampPanelGeometry({
    x: Math.max(viewportMargin, Math.round((window.innerWidth - width) / 2)),
    y: Math.max(viewportMargin, Math.round((window.innerHeight - height) / 2)),
    width,
    height,
  });
}

function clampPanelGeometry(geometry: PanelGeometry): PanelGeometry {
  const width = Math.min(Math.max(minPanelWidth, geometry.width), Math.max(minPanelWidth, window.innerWidth - viewportMargin * 2));
  const height = Math.min(Math.max(minPanelHeight, geometry.height), Math.max(minPanelHeight, window.innerHeight - viewportMargin * 2));
  return {
    width,
    height,
    x: Math.min(Math.max(viewportMargin, geometry.x), Math.max(viewportMargin, window.innerWidth - width - viewportMargin)),
    y: Math.min(Math.max(viewportMargin, geometry.y), Math.max(viewportMargin, window.innerHeight - height - viewportMargin)),
  };
}

function readStoredPanelGeometry(): PanelGeometry | undefined {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<PanelGeometry>;
    if (![parsed.x, parsed.y, parsed.width, parsed.height].every(value => typeof value === "number" && Number.isFinite(value))) {
      return undefined;
    }
    return parsed as PanelGeometry;
  } catch {
    return undefined;
  }
}

function storePanelGeometry(geometry: PanelGeometry): void {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(geometry));
  } catch {
    // Ignore quota / private mode failures.
  }
}
